import type { SentryApi } from '@core/analytics/interfaces/sentry';
import type { SentryEventDecision, SentryInjections, SentryReport } from '@core/analytics/types/sentry';
import { afterInteraction } from '../common/afterInteraction';
import {
  BROWSER_API_ERRORS_INTEGRATION_NAME,
  MAX_SENTRY_LOAD_ATTEMPTS,
  POLICY_TAG_NAME,
} from '../common/constants/sentry';
import { getSentryConfig } from '../common/sentry/sentryConfig';
import { checkEvent, getAppOrigin, maskPersonalData } from '../common/sentry/sentryEvent';
import { createConsoleLogger, createQueue, createSentryLogger, sendReport, toError } from '../common/sentry/sentryReports';

export default defineNuxtPlugin<SentryInjections>((nuxtApp) => {
  const runtimeConfig = useRuntimeConfig();
  const config = getSentryConfig(runtimeConfig.public);

  if (!config || config.isNonProduction) {
    return { provide: { logger: createConsoleLogger(), sentrySetUser: () => {} } };
  }

  const queue = createQueue();
  let sentry: SentryApi | null = null;
  let isLoading = false;
  let loadAttempts = 0;

  const onUncaught = (error: unknown) => {
    if (error instanceof Error) {
      send({ kind: 'error', error });
    }
  };

  const onWindowError = (event: ErrorEvent) => onUncaught(event.error);

  const onUnhandledRejection = (event: PromiseRejectionEvent) => onUncaught(event.reason);

  const stopCapturingUncaught = () => {
    globalThis.removeEventListener('error', onWindowError);
    globalThis.removeEventListener('unhandledrejection', onUnhandledRejection);
  };

  const startSentry = async () => {
    const appOrigin = getAppOrigin();
    const appAssetsUrl = appOrigin && new URL(runtimeConfig.app.buildAssetsDir, appOrigin).href;

    const { init, addBreadcrumb, captureException, setUser, withScope } = await import('@sentry/browser');
    const { browserTracingIntegration } = await import('@sentry/vue');
    const router = useRouter();

    init({
      dsn: config.dsn,
      environment: config.environment,
      release: nuxtApp.$releaseVersion as string | undefined,

      beforeSend(event, hint) {
        let decision: SentryEventDecision;

        try {
          decision = checkEvent(event, hint, appAssetsUrl);
        } catch {
          decision = { action: 'keep', reason: 'policy_failed' };
        }

        if (decision.action === 'drop' && !config.isShadowPolicy) {
          return null;
        }

        event.tags = { ...event.tags, [POLICY_TAG_NAME]: `${decision.action}:${decision.reason}` };

        try {
          return maskPersonalData(event);
        } catch {
          return null;
        }
      },

      integrations: defaults => [
        ...defaults.filter(({ name }) => name !== BROWSER_API_ERRORS_INTEGRATION_NAME),
        browserTracingIntegration({ router }),
      ],

      dataCollection: {
        userInfo: true,
      },

      sampleRate: 1,
      tracesSampleRate: 0.001,
      tracePropagationTargets: ['localhost'],
    });

    const readySentry = { addNote: addBreadcrumb, captureException, setUser, withScope };

    queue.drain().forEach(report => sendReport(readySentry, report));
    sentry = readySentry;
  };

  const loadSentry = () => {
    if (isLoading || loadAttempts >= MAX_SENTRY_LOAD_ATTEMPTS) {
      return;
    }

    isLoading = true;
    loadAttempts += 1;

    startSentry()
      .then(stopCapturingUncaught)
      .catch((error) => {
        isLoading = false;

        if (import.meta.dev) {
          console.warn('[sentry] failed to load:', error);
        }
      });
  };

  function send(report: SentryReport) {
    if (sentry) {
      sendReport(sentry, report);
      return;
    }

    queue.add(report);

    if (report.kind === 'error') {
      loadSentry();
    }
  }

  globalThis.addEventListener('error', onWindowError);
  globalThis.addEventListener('unhandledrejection', onUnhandledRejection);

  nuxtApp.hook('vue:error', error => send({ kind: 'error', error: toError(error) }));
  nuxtApp.hook('app:mounted', () => afterInteraction().then(loadSentry));

  return {
    provide: {
      logger: createSentryLogger(send),
      sentrySetUser: user => send({ kind: 'user', user }),
    },
  };
});
